<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import InspectionStep2View from '@/components/inspection/step2/InspectionStep2View.vue'
import { type Step2TabType } from '@/types/ui/inspection/tabsTypes'
import { STEP2_TAB } from '@/constants/ui/inspection/step2/tabs'
import { STEP2_TAB_LABELS } from '@/constants/ui/inspection/step2/tabsLabel'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { MODAL_RESULT } from '@/modules/miiaFw/types/components/commonModal'

export default defineComponent({
  name: 'InspectionStep2',
  components: {
    InspectionStep2View
  },
  props: {
    triggerValidate: {
      type: Boolean,
      default: false
    },
    saveToStoreFlag: {
      type: Boolean,
      default: false
    }
  },
  emit: ['onValidationResult', 'savedInspectionResultStore'],
  setup(props, { emit }) {
    const commonModalStore = useCommonModalStore()

    /** 表示するタブ */
    const activeTab = ref<Step2TabType>(STEP2_TAB.neighboring)

    /** 隣接建築物・周辺地盤等及び構造躯体タブの入力チェックのトリガ */
    const triggerCheckNeighboring = ref<boolean>(false)

    /** 隣接建築物・周辺地盤等及び構造躯体タブの入力チェック結果 */
    const checkNeighboringResult = ref<string[]>([])

    /** 落下危険物・店頭危険物タブの入力チェックのトリガ */
    const triggerCheckFalling = ref<boolean>(false)

    /** 落下危険物・店頭危険物タブの入力チェック結果 */
    const checkFallingResult = ref<string[]>([])

    /**
     * 隣接建築物・周辺地盤等及び構造躯体タブの入力チェック結果受け取り処理
     * @param result 入力チェック結果
     * @returns なし
     */
    const onNeighboringCheckResult = (result: string[]): void => {
      checkNeighboringResult.value = result
      triggerCheckNeighboring.value = false
    }

    /**
     * 落下危険物・店頭危険物タブの入力チェック結果受け取り処理
     * @param result 入力チェック結果
     * @returns なし
     */
    const onFallingCheckResult = (result: string[]): void => {
      checkFallingResult.value = result
      triggerCheckFalling.value = false
    }

    /**
     * 入力チェック結果をemitする
     * @param なし
     * @returns なし
     */
    const emitCheckResult = (): void => {
      // どちらかの調査タブでチェック中の場合は何もしない
      if (triggerCheckNeighboring.value || triggerCheckFalling.value) return
      // チェックの結果、未選択項目が無い場合
      if (checkNeighboringResult.value.length === 0 && checkFallingResult.value.length === 0) {
        emit('onValidationResult', true)
        return
      }

      const bodyParam = `このまま判定結果を表示しますか？<br>
        ${checkNeighboringResult.value.map(item => `・${item}`).join('<br>')}<br>
        ${checkFallingResult.value.map(item => `・${item}`).join('<br>')}`
      commonModalStore
        .openConfirmModal({
          title: '未選択の項目があります',
          body: bodyParam,
          okTitle: 'はい',
          cancelTitle: 'いいえ'
        })
        .then(async result => {
          if (result === MODAL_RESULT.OK) {
            emit('onValidationResult', true)
          }
        })
        .catch(async result => {
          if (result === MODAL_RESULT.CANCEL) {
            emit('onValidationResult', false)
          }
        })
    }

    /**
     * 調査結果Storeの更新を親コンポーネントに通知
     * @param なし
     * @returns なし
     */
    const savedInspectionResultStore = (): void => {
      emit('savedInspectionResultStore')
    }

    // 未選択項目が存在するかのチェックを開始する
    watch(
      () => props.triggerValidate,
      newValue => {
        if (newValue) {
          triggerCheckNeighboring.value = true
          triggerCheckFalling.value = true
        }
      }
    )

    watch(
      () => triggerCheckNeighboring.value,
      newValue => {
        // チェックフラグがtrueの場合、チェック中のため何もしない
        if (newValue) return
        emitCheckResult()
      }
    )

    watch(
      () => triggerCheckFalling.value,
      newValue => {
        // チェックフラグがtrueの場合、チェック中のため何もしない
        if (newValue) return
        emitCheckResult()
      }
    )

    return {
      activeTab,
      step2TabLabels: STEP2_TAB_LABELS,
      STEP2_TAB,
      triggerCheckNeighboring,
      triggerCheckFalling,
      onNeighboringCheckResult,
      onFallingCheckResult,
      savedInspectionResultStore
    }
  }
})
</script>

<template>
  <div class="container mt-5 pb-6">
    <!-- 調査 Step2 タイトル -->
    <div class="d-flex justify-content-between border-bottom border-dark mb-4 pb-4">
      <h1 class="text-xl font-weight-bold flex-grow">
        調査 Step2 <span class="text-danger">【必須】</span>
      </h1>
    </div>
    <!-- タブナビゲーション -->
    <div class="d-flex mb-4">
      <!-- 隣接建築物ナビ -->
      <div
        @click="activeTab = STEP2_TAB.neighboring"
        class="flex-fill text-center py-3 cursor-pointer text-sm fw-medium fs-4 fw-bold"
        :class="{
          'active-tab shadow': activeTab === STEP2_TAB.neighboring,
          'inactive-tab bg-light': activeTab !== STEP2_TAB.neighboring
        }"
      >
        {{ step2TabLabels.neighboring }}
      </div>
      <!-- 落下危険物ナビ -->
      <div
        @click="activeTab = STEP2_TAB.falling"
        class="flex-fill text-center py-3 cursor-pointer text-sm fw-medium fs-4 fw-bold"
        :class="{
          'active-tab shadow': activeTab === STEP2_TAB.falling,
          'inactive-tab bg-light': activeTab !== STEP2_TAB.falling
        }"
      >
        {{ step2TabLabels.falling }}
      </div>
    </div>
    <!-- タブのコンテンツ -->
    <div class="p-4">
      <div v-show="activeTab === STEP2_TAB.neighboring">
        <InspectionStep2View
          :inspectionType="STEP2_TAB.neighboring"
          :triggerCheckNeighboring="triggerCheckNeighboring"
          :saveToStoreFlag="saveToStoreFlag"
          @onNeighboringCheckResult="onNeighboringCheckResult"
          @savedInspectionResultStore="savedInspectionResultStore"
        />
      </div>
      <div v-show="activeTab === STEP2_TAB.falling">
        <InspectionStep2View
          :inspectionType="STEP2_TAB.falling"
          :triggerCheckFalling="triggerCheckFalling"
          :saveToStoreFlag="saveToStoreFlag"
          @onFallingCheckResult="onFallingCheckResult"
          @savedInspectionResultStore="savedInspectionResultStore"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.active-tab {
  background-color: #2c52cf !important;
  color: #ffffff;
}
.inactive-tab {
  color: gray;
}
</style>
